import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Trophy, Users, ClipboardList, Activity, CheckCircle2, Clock, UserCog, Plus } from "lucide-react";

export default function Instructor() {
  const { user } = useAuth();
  const [tab, setTab] = useState("attempts");
  const [attempts, setAttempts] = useState([]);
  const [board, setBoard] = useState([]);
  const [users, setUsers] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [asgScenario, setAsgScenario] = useState("");
  const [asgUser, setAsgUser] = useState("");

  const loadAll = useCallback(() => {
    api.get("/instructor/attempts").then((r) => setAttempts(r.data)).catch(() => {});
    api.get("/instructor/leaderboard").then((r) => setBoard(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/scenarios").then((r) => setScenarios(r.data)).catch(() => {});
    api.get("/assignments").then((r) => setAssignments(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const createAssignment = async () => {
    if (!asgScenario || !asgUser) { toast.error("Pick a scenario and a trainee"); return; }
    try {
      await api.post("/assignments", { scenario_id: asgScenario, assigned_to: [asgUser] });
      toast.success("Assignment created");
      setAssignOpen(false); setAsgScenario(""); setAsgUser("");
      loadAll();
    } catch { toast.error("Failed to assign"); }
  };

  const changeRole = async (uid, role) => {
    try { await api.put(`/admin/users/${uid}/role`, { role }); toast.success("Role updated"); loadAll(); }
    catch { toast.error("Only admins can change roles"); }
  };

  return (
    <Layout>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="label-ot text-primary">Instructor Console</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Training Operations</h1>
        </div>
        <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
          <DialogTrigger asChild>
            <Button data-testid="open-assign-button" className="rounded-sm font-semibold"><Plus className="w-4 h-4 mr-2" />Assign Scenario</Button>
          </DialogTrigger>
          <DialogContent className="bg-popover border-border">
            <DialogHeader><DialogTitle className="font-heading">Assign a Scenario</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <label className="label-ot block mb-1.5">Scenario</label>
                <Select value={asgScenario} onValueChange={setAsgScenario}>
                  <SelectTrigger data-testid="assign-scenario-select" className="rounded-sm bg-card border-border"><SelectValue placeholder="Select scenario" /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {scenarios.map((s) => <SelectItem key={s.scenario_id} value={s.scenario_id}>{s.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="label-ot block mb-1.5">Trainee</label>
                <Select value={asgUser} onValueChange={setAsgUser}>
                  <SelectTrigger data-testid="assign-user-select" className="rounded-sm bg-card border-border"><SelectValue placeholder="Select trainee" /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="confirm-assign-button" onClick={createAssignment} className="rounded-sm font-semibold w-full">Create Assignment</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-card border border-border/60 rounded-sm mb-4">
          <TabsTrigger value="attempts" data-testid="tab-attempts" className="rounded-sm text-xs"><Activity className="w-3.5 h-3.5 mr-1.5" />Attempts</TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="tab-leaderboard" className="rounded-sm text-xs"><Trophy className="w-3.5 h-3.5 mr-1.5" />Leaderboard</TabsTrigger>
          <TabsTrigger value="assignments" data-testid="tab-assignments" className="rounded-sm text-xs"><ClipboardList className="w-3.5 h-3.5 mr-1.5" />Assignments</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users" className="rounded-sm text-xs"><Users className="w-3.5 h-3.5 mr-1.5" />Users</TabsTrigger>
        </TabsList>

        {/* Attempts */}
        <TabsContent value="attempts">
          <div className="bg-card border border-border/60 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border/60 text-left label-ot">
                <th className="px-4 py-2.5">Trainee</th><th className="px-4 py-2.5">Scenario</th>
                <th className="px-4 py-2.5 hidden sm:table-cell">Type</th><th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5 text-right">Score</th>
              </tr></thead>
              <tbody>
                {attempts.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-6 text-muted-foreground text-sm">No attempts recorded yet.</td></tr>
                ) : attempts.map((a) => (
                  <tr key={a.attempt_id} data-testid="instructor-attempt-row" className="border-b border-border/30 last:border-0 hover:bg-secondary/30">
                    <td className="px-4 py-2.5 font-medium">{a.user_name}</td>
                    <td className="px-4 py-2.5">{a.scenario_title}</td>
                    <td className="px-4 py-2.5 hidden sm:table-cell font-mono text-xs uppercase text-muted-foreground">{a.type}</td>
                    <td className="px-4 py-2.5">{a.status === "completed"
                      ? <span className="inline-flex items-center gap-1.5 text-xs text-status-normal"><CheckCircle2 className="w-3.5 h-3.5" />Completed</span>
                      : <span className="inline-flex items-center gap-1.5 text-xs text-status-warning"><Clock className="w-3.5 h-3.5" />In progress</span>}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{a.status === "completed" ? `${a.score}/${a.max_score}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Leaderboard */}
        <TabsContent value="leaderboard">
          <div className="bg-card border border-border/60 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border/60 text-left label-ot">
                <th className="px-4 py-2.5">#</th><th className="px-4 py-2.5">Trainee</th>
                <th className="px-4 py-2.5 text-right">Completed</th><th className="px-4 py-2.5 text-right">Avg</th><th className="px-4 py-2.5 text-right">Best</th>
              </tr></thead>
              <tbody>
                {board.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-6 text-muted-foreground text-sm">No completed attempts yet.</td></tr>
                ) : board.map((r, i) => (
                  <tr key={r.user_id} data-testid="leaderboard-row" className="border-b border-border/30 last:border-0 hover:bg-secondary/30">
                    <td className="px-4 py-2.5 font-mono">{i + 1}</td>
                    <td className="px-4 py-2.5 font-medium">{r.user_name}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{r.completed}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-status-warning">{r.avg_score}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{r.best}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Assignments */}
        <TabsContent value="assignments">
          <div className="bg-card border border-border/60 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border/60 text-left label-ot">
                <th className="px-4 py-2.5">Trainee</th><th className="px-4 py-2.5">Scenario</th><th className="px-4 py-2.5">Assigned by</th>
              </tr></thead>
              <tbody>
                {assignments.length === 0 ? (
                  <tr><td colSpan={3} className="px-4 py-6 text-muted-foreground text-sm">No assignments yet.</td></tr>
                ) : assignments.map((a) => (
                  <tr key={a.assignment_id} data-testid="assignment-row" className="border-b border-border/30 last:border-0 hover:bg-secondary/30">
                    <td className="px-4 py-2.5 font-medium">{a.assignee?.name || a.assigned_to}</td>
                    <td className="px-4 py-2.5">{a.scenario?.title}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{a.assigned_by_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Users */}
        <TabsContent value="users">
          <div className="bg-card border border-border/60 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border/60 text-left label-ot">
                <th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5 hidden sm:table-cell">Email</th><th className="px-4 py-2.5">Role</th>
              </tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} data-testid="user-row" className="border-b border-border/30 last:border-0 hover:bg-secondary/30">
                    <td className="px-4 py-2.5 font-medium">{u.name}</td>
                    <td className="px-4 py-2.5 hidden sm:table-cell font-mono text-xs text-muted-foreground">{u.email}</td>
                    <td className="px-4 py-2.5">
                      {user.role === "admin" ? (
                        <Select value={u.role} onValueChange={(v) => changeRole(u.user_id, v)}>
                          <SelectTrigger data-testid={`role-select-${u.user_id}`} className="h-7 w-32 rounded-sm bg-card border-border text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-popover border-border">
                            <SelectItem value="trainee">trainee</SelectItem>
                            <SelectItem value="instructor">instructor</SelectItem>
                            <SelectItem value="admin">admin</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs font-mono"><UserCog className="w-3.5 h-3.5 text-muted-foreground" />{u.role}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
